"use client"

import { useState } from "react"
import { ChevronDown } from "lucide-react"

const faqs = [
  {
    question: "Do I need to subscribe to external APIs?",
    answer:
      "Yes — and that's intentional. You own the entire system, which means you hold your own API keys for Binance, Selcom, and RapidAPI. This eliminates any dependency on us. Estimated external API costs are $25–$30/month, paid directly to the providers. We never see or touch your credentials.",
  },
  {
    question: "Is the Android app fully rebranded under my name?",
    answer:
      "Completely. The app will carry your brand name, logo, color scheme, and Play Store listing. Your end-users will never know the underlying technology was built by Mocode Studio. You even get the source code.",
  },
  {
    question: "What is the difference between the User App and the Admin App?",
    answer:
      "The User App is the customer-facing app where your clients deposit TZS and see their USDT balance. The Admin App is your private management tool — it shows daily trading volume, profit from spreads, payment logs, bot status, and suspicious activity alerts. Two separate apps, one unified backend.",
  },
  {
    question: "How quickly can I go live after purchase?",
    answer:
      "Most merchants go live within 48 hours. We provide hands-on deployment support, walk you through server setup, API key configuration, and your first test transaction. You don't need a developer background — our guide covers every step.",
  },
  {
    question: "Do I need a Binance verified business account?",
    answer:
      "You'll need a Binance account that is P2P-verified and has sufficient USDT liquidity to fill orders. We recommend starting with at least $3,000–$5,000 USDT. The system is designed for high-volume merchants, so the more liquidity you have, the more trades you can automate.",
  },
  {
    question: "What happens if a user's mobile money payment fails?",
    answer:
      "The Advanced Risk Engine handles failed or unverified payments automatically. The system will not execute a Binance trade unless payment confirmation is verified by the gateway. Failed transactions are logged in the Admin App with full details for your review.",
  },
  {
    question: "Is there any revenue share or commission taken by AutoP2P?",
    answer:
      "None whatsoever. After the one-time $1,000 setup fee, 100% of your trading profits belong to you. There is no revenue share, no commission on trades, and no hidden fees. This is your business — we just build the engine.",
  },
  {
    question: "Which countries are supported?",
    answer:
      "Currently, the system is optimized for Tanzania (M-Pesa, Airtel Money, Selcom/Tigopesa). Expansion to Kenya (M-Pesa KE), Uganda, and Rwanda is on the roadmap. If you're in a neighboring country, reach out — we may be able to accommodate a custom integration.",
  },
]

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section id="faq" className="py-24 px-6 bg-[#0f0f1a]/50">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.03] mb-5">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#8892a4]">
              FAQ
            </span>
          </div>
          <h2
            className="text-3xl md:text-4xl font-extrabold text-[#f8fafc] text-balance"
            style={{ fontFamily: "var(--font-space-grotesk, sans-serif)" }}
          >
            Every question. Answered.
          </h2>
          <p className="mt-4 text-[#8892a4] leading-relaxed">
            We believe in full transparency. If your question isn&apos;t here, reach out directly.
          </p>
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index
            return (
              <div
                key={index}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? "border-[#00e5ff]/25 bg-[#141428]"
                    : "border-white/[0.07] bg-[#141422] hover:border-white/[0.12]"
                }`}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                >
                  <span
                    className={`text-sm font-semibold leading-snug transition-colors ${
                      isOpen ? "text-[#f8fafc]" : "text-[#c8d0e0]"
                    }`}
                    style={{ fontFamily: "var(--font-space-grotesk, sans-serif)" }}
                  >
                    {faq.question}
                  </span>
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isOpen
                        ? "bg-[#00e5ff]/15 border border-[#00e5ff]/30 rotate-180"
                        : "bg-white/[0.05] border border-white/[0.08]"
                    }`}
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-colors ${
                        isOpen ? "text-[#00e5ff]" : "text-[#8892a4]"
                      }`}
                    />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-5">
                    <div className="h-px bg-white/[0.05] mb-4" />
                    <p className="text-sm text-[#8892a4] leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
